import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, handleApiError } from '../client';
import type { ApiError } from '../types/common';

// Query keys
const verificationKeys = {
  all: ['verifications'] as const,
  list: (filters?: VerificationFilters) => [...verificationKeys.all, 'list', filters] as const,
  detail: (id: string) => [...verificationKeys.all, 'detail', id] as const,
};

// Types
export interface VerificationFilters {
  status?: 'PENDING' | 'APPROVED' | 'REJECTED' | 'NEEDS_INFO';
  page?: number;
  limit?: number;
}

export interface TherapistVerification {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  verificationStatus: string;
  documentCount: number;
  submittedAt?: string;
  verifiedAt?: string;
  verifiedBy?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  rejectionReason?: string;
  createdAt: string;
}

export interface VerificationDocument {
  id: string;
  documentType: string;
  fileName: string;
  contentType: string;
  fileSize: number;
  status: string;
  uploadedAt: string;
  processedAt?: string;
  extractedData?: Record<string, any>;
}

export interface VerificationHistoryItem {
  id: string;
  note: string;
  action: string;
  createdAt: string;
  admin: {
    firstName: string;
    lastName: string;
  };
}

export interface VerificationDetail extends TherapistVerification {
  bio?: string;
  professionalInfo?: {
    npiNumber?: string;
    licenseType?: string;
    licenseState?: string;
    licenseNumber?: string;
  };
  documents: VerificationDocument[];
  history: VerificationHistoryItem[];
  updatedAt: string;
}

export interface VerificationsListResponse {
  verifications: TherapistVerification[];
  page: number;
  limit: number;
  count: number;
}

export interface ApproveVerificationRequest {
  note?: string;
}

export interface RejectVerificationRequest {
  reason: string;
  note?: string;
}

export interface RequestInfoRequest {
  message: string;
}

// API Functions
async function fetchVerifications(filters?: VerificationFilters): Promise<VerificationsListResponse> {
  const params = new URLSearchParams();

  if (filters?.status) {
    params.append('status', filters.status);
  }
  if (filters?.page) {
    params.append('page', filters.page.toString());
  }
  if (filters?.limit) {
    params.append('limit', filters.limit.toString());
  }

  const queryString = params.toString();
  const url = `/admin/verifications${queryString ? `?${queryString}` : ''}`;

  const response = await apiClient.get<VerificationsListResponse>(url);
  return response.data;
}

async function fetchVerificationDetail(id: string): Promise<VerificationDetail> {
  const response = await apiClient.get<VerificationDetail>(`/admin/verifications/${id}`);
  return response.data;
}

async function approveVerification(id: string, data: ApproveVerificationRequest): Promise<void> {
  await apiClient.post(`/admin/verifications/${id}/approve`, data);
}

async function rejectVerification(id: string, data: RejectVerificationRequest): Promise<void> {
  await apiClient.post(`/admin/verifications/${id}/reject`, data);
}

async function requestMoreInfo(id: string, data: RequestInfoRequest): Promise<void> {
  await apiClient.post(`/admin/verifications/${id}/request-info`, data);
}

// Hooks
export function useVerificationQueue(filters?: VerificationFilters) {
  return useQuery({
    queryKey: verificationKeys.list(filters),
    queryFn: () => fetchVerifications(filters),
    staleTime: 30 * 1000, // 30 seconds
    retry: 2,
  });
}

export function useVerificationDetail(id: string) {
  return useQuery({
    queryKey: verificationKeys.detail(id),
    queryFn: () => fetchVerificationDetail(id),
    enabled: !!id,
    staleTime: 30 * 1000, // 30 seconds
    retry: 2,
  });
}

export function useApproveVerification() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, { id: string; data: ApproveVerificationRequest }>({
    mutationFn: ({ id, data }) => approveVerification(id, data),
    onSuccess: (_, { id }) => {
      // Invalidate verification lists and detail
      queryClient.invalidateQueries({ queryKey: verificationKeys.all });
      queryClient.invalidateQueries({ queryKey: verificationKeys.detail(id) });
    },
    onError: (error) => {
      console.error('Approve verification failed:', error);
    },
  });
}

export function useRejectVerification() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, { id: string; data: RejectVerificationRequest }>({
    mutationFn: ({ id, data }) => rejectVerification(id, data),
    onSuccess: (_, { id }) => {
      // Invalidate verification lists and detail
      queryClient.invalidateQueries({ queryKey: verificationKeys.all });
      queryClient.invalidateQueries({ queryKey: verificationKeys.detail(id) });
    },
    onError: (error) => {
      console.error('Reject verification failed:', error);
    },
  });
}

export function useRequestMoreInfo() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, { id: string; data: RequestInfoRequest }>({
    mutationFn: ({ id, data }) => requestMoreInfo(id, data),
    onSuccess: (_, { id }) => {
      // Invalidate verification lists and detail
      queryClient.invalidateQueries({ queryKey: verificationKeys.all });
      queryClient.invalidateQueries({ queryKey: verificationKeys.detail(id) });
    },
    onError: (error) => {
      console.error('Request more info failed:', error);
    },
  });
}
